import ExcelJS, { Alignment, Fill, Worksheet } from "exceljs";
import { saveAs } from "file-saver";

import {
  EncargoViewType,
  EncargosProps,
  TaxHistoryData,
  TaxProjectionData,
  TaxSuggestionData,
} from "../Dashboard/EncargosAccordion";

interface Balance {
  agentName: string;
  companyBalance: number | null;
  lastBalanceUpdate: string | null;
}

const HEADER_FILL: Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: {
    argb: "FFF5F5F5",
  },
};

const HEADER_ALIGNMENT: Partial<Alignment> = {
  horizontal: "center",
  vertical: "middle",
};

const HISTORICAL_SUGGESTION_NOTICE =
  "A sugestão de aporte está disponível apenas para o mês anterior e o vigente.";

const CURRENCY_FORMAT = '"R$" #,##0.00';

const formatMonthYear = (month: number, year: number) =>
  `${String(month).padStart(2, "0")}/${year}`;

const formatDate = (date: string) =>
  new Date(date).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function applyCurrencyColumns(row: ExcelJS.Row, columns: number[]) {
  columns.forEach((column) => {
    const cell = row.getCell(column);

    if (typeof cell.value === "number") {
      cell.numFmt = CURRENCY_FORMAT;
      cell.alignment = {
        horizontal: "right",
      };
    }
  });
}

function addHeaderRow(worksheet: Worksheet, headers: string[]) {
  const row = worksheet.addRow(headers);

  row.eachCell((cell) => {
    cell.font = {
      bold: true,
    };

    cell.alignment = HEADER_ALIGNMENT;

    cell.fill = HEADER_FILL;
  });

  row.height = 22;
}

function setColumnWidths(worksheet: Worksheet, widths: number[]) {
  worksheet.columns = widths.map((width) => ({
    width,
  }));
}

function freezeHeader(worksheet: Worksheet) {
  worksheet.views = [
    {
      state: "frozen",
      ySplit: 1,
    },
  ];
}

function applyAutoFilter(worksheet: Worksheet, lastColumn: string) {
  worksheet.autoFilter = {
    from: "A1",
    to: `${lastColumn}1`,
  };
}

function configureWorksheet(
  worksheet: Worksheet,
  columnWidths: number[],
  lastColumn: string
) {
  setColumnWidths(worksheet, columnWidths);

  freezeHeader(worksheet);

  applyAutoFilter(worksheet, lastColumn);
}

function buildEncargosSheet(
  worksheet: Worksheet,
  data: TaxHistoryData[],
  type: EncargoViewType
) {
  configureWorksheet(worksheet, [35, 18, 18, 18, 18, 18, 18, 18, 22], "I");

  addHeaderRow(worksheet, [
    type === "agent" ? "Agente" : "Unidade",
    "Mês/Ano",
    "Boleto associativo",
    "Energia de reserva",
    "Garantia financeira",
    "Reserva de capacidade",
    "Cota nuclear",
    "Crédito MCP",
    "Total encargos sem boleto",
  ]);

  data.forEach((item) => {
    const row = worksheet.addRow([
      type === "agent"
        ? item.agentName
        : item.name.split(" - ").slice(1).join(" - "),

      formatMonthYear(item.month, item.year),

      item.associativeContributionTax,

      item.energyReserveTax,

      item.financialGuaranteeTax,

      item.capacityReserveTax,

      item.quotaNuclearTax,

      item.mcpCreditTax,

      item.totalWithoutAssociative,
    ]);

    applyCurrencyColumns(row, [3, 4, 5, 6, 7, 8, 9]);
  });

  // Centraliza o cabeçalho
  worksheet.getRow(1).alignment = {
    vertical: "middle",
    horizontal: "center",
  };

  // Centraliza o mês/ano
  worksheet.eachRow((row, index) => {
    if (index === 1) return;

    row.getCell(2).alignment = {
      horizontal: "center",
    };
  });

  // Autoajusta altura das linhas
  worksheet.eachRow((row) => {
    row.height = 20;
  });
}

function buildProjectionSheet(
  worksheet: Worksheet,
  projection: TaxProjectionData[],
  finalized: TaxHistoryData[],
  balances: Balance[],
  suggestion: TaxSuggestionData[],
  isSuggestionAvailable: boolean
) {
  configureWorksheet(worksheet, [30, 20, 20, 20, 20, 20, 20], "G");

  addHeaderRow(worksheet, [
    "Agente",
    "Tipo",
    "Energia de reserva",
    "Garantia financeira",
    "Reserva de capacidade",
    "Cota nuclear",
    "Total de encargos",
  ]);

  projection.forEach((item) => {
    const consolidated = finalized.find(
      (final) =>
        final.agentName === item.agentName &&
        final.month === item.month &&
        final.year === item.year
    );

    const balance = balances.find((b) => b.agentName === item.agentName);
    const agentSuggestion = suggestion.find(
      (s) => s.agentName === item.agentName
    );

    const projectionRow = worksheet.addRow([
      item.agentName,
      item.type,
      item.energyReserveTax,
      item.financialGuaranteeTax,
      item.capacityReserveTax,
      item.quotaNuclearTax,
      item.totalChargesExcludingMcpCredit,
    ]);

    applyCurrencyColumns(projectionRow, [3, 4, 5, 6, 7]);

    const consolidatedRow = worksheet.addRow([
      item.agentName,
      "Consolidado",

      consolidated ? consolidated.energyReserveTax : "-",

      consolidated ? consolidated.financialGuaranteeTax : "-",

      consolidated ? consolidated.capacityReserveTax : "-",

      consolidated ? consolidated.quotaNuclearTax : "-",

      consolidated ? consolidated.totalChargesExcludingMcpCredit : "-",
    ]);

    if (consolidated) {
      applyCurrencyColumns(consolidatedRow, [3, 4, 5, 6, 7]);
    }

    if (consolidated) {
      const differenceRow = worksheet.addRow([
        item.agentName,
        "Diferença",

        item.energyReserveTax - consolidated.energyReserveTax,

        item.financialGuaranteeTax - consolidated.financialGuaranteeTax,

        item.capacityReserveTax - consolidated.capacityReserveTax,

        item.quotaNuclearTax - consolidated.quotaNuclearTax,

        item.totalChargesExcludingMcpCredit -
          consolidated.totalChargesExcludingMcpCredit,
      ]);

      applyCurrencyColumns(differenceRow, [3, 4, 5, 6, 7]);
    }

    if (balance) {
      const companyBalance = balance.companyBalance ?? 0;
      const balanceRow = worksheet.addRow([
        `Saldo ${balance.agentName}`,
        companyBalance,
        "Última atualização",
        balance.lastBalanceUpdate
          ? formatDate(balance.lastBalanceUpdate)
          : "Nunca foi atualizado",
        "",
        "Sugestão de aporte",
        isSuggestionAvailable
          ? agentSuggestion?.amount ?? "-"
          : HISTORICAL_SUGGESTION_NOTICE,
      ]);

      balanceRow.font = {
        bold: true,
      };

      balanceRow.getCell(2).numFmt = CURRENCY_FORMAT;

      if (isSuggestionAvailable && agentSuggestion) {
        balanceRow.getCell(7).numFmt = CURRENCY_FORMAT;
      } else if (!isSuggestionAvailable) {
        balanceRow.getCell(7).fill = HEADER_FILL;
      }

      balanceRow.getCell(2).alignment = {
        horizontal: "right",
      };

      balanceRow.getCell(7).alignment = {
        horizontal: "right",
        wrapText: !isSuggestionAvailable,
      };

      worksheet.addRow([]);
    }
  });

  worksheet.eachRow((row, index) => {
    row.height =
      !isSuggestionAvailable && row.getCell(6).value === "Sugestão de aporte"
        ? 90
        : 20;

    if (index === 1) return;

    row.getCell(2).alignment = {
      horizontal: "center",
    };
  });
}

function buildProjectionByUnitSheet(
  worksheet: Worksheet,
  projectionByUnit: TaxProjectionData[]
) {
  configureWorksheet(worksheet, [30, 35, 18, 18, 18, 18, 20], "G");

  addHeaderRow(worksheet, [
    "Agente",
    "Unidade",
    "Energia de reserva",
    "Garantia financeira",
    "Reserva de capacidade",
    "Cota nuclear",
    "Total de encargos",
  ]);

  projectionByUnit.forEach((item) => {
    const row = worksheet.addRow([
      item.agentName,
      item.name,
      item.energyReserveTax,
      item.financialGuaranteeTax,
      item.capacityReserveTax,
      item.quotaNuclearTax,
      item.totalChargesExcludingMcpCredit,
    ]);

    applyCurrencyColumns(row, [3, 4, 5, 6, 7]);
  });

  worksheet.getRow(1).alignment = {
    vertical: "middle",
    horizontal: "center",
  };

  worksheet.eachRow((row) => {
    row.height = 20;
  });
}

export async function exportEncargosExcel(
  data: EncargosProps,
  balances: Balance[]
) {
  const workbook = new ExcelJS.Workbook();

  workbook.creator = "Sua Aplicação";
  workbook.lastModifiedBy = "Sua Aplicação";
  workbook.created = new Date();
  workbook.modified = new Date();

  const byAgentWorksheet = workbook.addWorksheet("Por Agente", {
    properties: {
      defaultRowHeight: 20,
    },
  });

  buildEncargosSheet(byAgentWorksheet, data.byAgent, "agent");

  const byUnitWorksheet = workbook.addWorksheet("Por Unidade", {
    properties: {
      defaultRowHeight: 20,
    },
  });

  buildEncargosSheet(byUnitWorksheet, data.byUnit, "unit");

  const projectionWorksheet = workbook.addWorksheet("Projeção", {
    properties: {
      defaultRowHeight: 20,
    },
  });

  buildProjectionSheet(
    projectionWorksheet,
    data.projection ?? [],
    data.byAgent,
    balances,
    data.suggestion ?? [],
    data.isSuggestionAvailable
  );

  projectionWorksheet.addRow([
    "Competência da projeção",
    formatMonthYear(data.projectionMonth, data.projectionYear),
  ]);
  if (!data.isSuggestionAvailable) {
    const noticeRow = projectionWorksheet.addRow([
      HISTORICAL_SUGGESTION_NOTICE,
    ]);
    projectionWorksheet.mergeCells(noticeRow.number, 1, noticeRow.number, 7);
    noticeRow.font = { bold: true };
  }

  const projectionByUnitWorksheet = workbook.addWorksheet(
    "Projeção por Unidade",
    {
      properties: {
        defaultRowHeight: 20,
      },
    }
  );

  buildProjectionByUnitSheet(
    projectionByUnitWorksheet,
    data.projectionByUnit ?? []
  );

  workbook.eachSheet((worksheet) => {
    worksheet.views = [
      {
        state: "frozen",
        ySplit: 1,
      },
    ];
  });

  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const today = new Date();
  const fileDate = [today.getDate(), today.getMonth() + 1, today.getFullYear()]
    .map((n) => String(n).padStart(2, "0"))
    .join("-");

  saveAs(
    blob,
    `Encargos_Projecao_${String(data.projectionMonth).padStart(2, "0")}-${
      data.projectionYear
    }_${fileDate}.xlsx`
  );
}
